// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { AutoSizer, Column } from 'react-virtualized';

import type { CatalogType, Dispatch } from 'state/types';
import DialogPanel from 'view/App/DialogPanels/DialogPanel';
import {
  deleteCatalogTemplate,
  renameCatalogTemplate,
} from 'state/commands/catalogCommands';
import {
  hideDialogPanel,
  showDialogPanel,
} from 'state/commands/dialogPanelCommands';
import { getActivePanelId, getCustomCatalogTemplates } from 'state/selectors';
import { CATALOG_TEMPLATES_PANEL_ID } from 'util/constants';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import localizedStrings from '../../../util/localizedStrings';
import MultipleSelectionTable from 'view/App/MultipleSelectionTable';
import {
  getNewSelectionState,
  getIndexOfFirstSelectedItem,
  getSelectedIndices,
  type SelectionState,
} from 'util/tableUtils';
import ModalStringPrompt from 'view/App/DialogPanels/ModalStringPrompt';

import { actions as modalPromptActions } from 'portfolio-shared/state/reducers/modalPromptReducer';
const { showModalPrompt } = modalPromptActions;

type Props = {
  activePanelId: ?string,
  deleteCatalogTemplate: typeof deleteCatalogTemplate,
  renameCatalogTemplate: typeof renameCatalogTemplate,
  hideDialogPanel: typeof hideDialogPanel,
  intl: IntlShape,
  catalogTemplates: CatalogType[],
  showDialogPanel: typeof showDialogPanel,
  showModalPrompt: typeof showModalPrompt,
};

type State = {
  isRenameVisible: boolean,
  selectionState: SelectionState,
};

class CatalogTemplatesDialog extends Component<Props, State> {
  doDelete: Function;
  doRename: Function;
  getSelectedTemplate: Function;
  handleCloseRename: Function;
  handleDeleteTemplate: Function;
  handleDoubleClick: Function;
  handleSelectionChanged: Function;
  renderPanelHeader: Function;
  renderTableCell: Function;
  templateRenameValidator: Function;

  state = {
    isRenameVisible: false,
    selectionState: getNewSelectionState(),
  };

  constructor() {
    super();
    this.doDelete = this.doDelete.bind(this);
    this.doRename = this.doRename.bind(this);
    this.getSelectedTemplate = this.getSelectedTemplate.bind(this);
    this.handleCloseRename = this.handleCloseRename.bind(this);
    this.handleDeleteTemplate = this.handleDeleteTemplate.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    this.handleSelectionChanged = this.handleSelectionChanged.bind(this);
    this.renderPanelHeader = this.renderPanelHeader.bind(this);
    this.renderTableCell = this.renderTableCell.bind(this);
    this.templateRenameValidator = this.templateRenameValidator.bind(this);
  }

  handleSelectionChanged(selectionState: SelectionState) {
    this.setState({ selectionState });
  }

  getSelectedTemplate() {
    const { selectionState } = this.state;
    const { catalogTemplates } = this.props;
    const index = getIndexOfFirstSelectedItem(selectionState);
    const ix = Number(index);
    return catalogTemplates[ix];
  }

  handleDeleteTemplate() {
    const template = this.getSelectedTemplate();
    if (template) {
      this.props.showModalPrompt({
        message: localizedStrings.CATALOG_TEMPLATE_REMOVE_CONFIRMATION,
        onButton2: this.doDelete,
        closeOnEscape: true,
        isButton2Default: true,
      });
    }
  }

  async doDelete() {
    const template = this.getSelectedTemplate();
    await this.props.deleteCatalogTemplate(template.guid);
    this.setState({ selectionState: getNewSelectionState() });
  }

  handleDoubleClick() {
    this.setState({ isRenameVisible: true });
  }

  async doRename(name) {
    const template = this.getSelectedTemplate();
    await this.props.renameCatalogTemplate(template.guid, name);
  }

  handleCloseRename() {
    this.setState({ isRenameVisible: false });
  }

  templateRenameValidator(value) {
    if (!value || !value.length) {
      return 'Empty String';
    } else if (
      this.props.catalogTemplates
        .map(t => t.name.toLowerCase())
        .includes(value.toLowerCase())
    ) {
      return 'Existing Template';
    }
  }

  renderPanelHeader() {
    const deleteDisabled =
      getSelectedIndices(this.state.selectionState).length === 0;

    return (
      <header className="page-header">
        <div style={{ width: '36px' }} />
        <FormattedMessage
          {...localizedStrings.CATALOG_MANAGE_TEMPLATES_DIALOG_TITLE}
        />
        <a
          className="button is-dark is-outlined is-inverted"
          disabled={deleteDisabled}
          onClick={deleteDisabled ? undefined : this.handleDeleteTemplate}
          data-test-name="delete-template"
        >
          <span className="icon">
            <i className="fa fa-lg fa-minus-circle" />
          </span>
        </a>
      </header>
    );
  }

  renderTableCell({ cellData }) {
    return <div onDoubleClick={this.handleDoubleClick}>{cellData}</div>;
  }

  render() {
    const {
      activePanelId,
      catalogTemplates,
      hideDialogPanel,
      intl: { formatMessage },
    } = this.props;
    const { selectionState } = this.state;
    return (
      <DialogPanel
        activePanelId={activePanelId}
        contentStyle={{ width: '700px', height: '500px' }}
        customButtonText={formatMessage(localizedStrings.OK)}
        hideDialogPanel={hideDialogPanel}
        panelHeader={this.renderPanelHeader()}
        panelId={CATALOG_TEMPLATES_PANEL_ID}
        showCancel={false}
      >
        <div className="main">
          <AutoSizer>
            {({ height, width }) => (
              <MultipleSelectionTable
                headerHeight={40}
                height={height}
                onSelectionChanged={this.handleSelectionChanged}
                rowHeight={40}
                selectionState={selectionState}
                singleSelectionOnly={true}
                sortedItems={catalogTemplates}
                width={width}
              >
                <Column
                  cellRenderer={this.renderTableCell}
                  className="list-item"
                  dataKey="name"
                  label={formatMessage(localizedStrings.CATALOG_TEMPLATE_NAME)}
                  width={width}
                />
              </MultipleSelectionTable>
            )}
          </AutoSizer>
        </div>
        <ModalStringPrompt
          initialValue={
            this.getSelectedTemplate() && this.getSelectedTemplate().name
          }
          isOpen={this.state.isRenameVisible}
          label={formatMessage(localizedStrings.NAME)}
          onClose={this.handleCloseRename}
          onSave={this.doRename}
          panelTitle={formatMessage(localizedStrings.CATALOG_TEMPLATE_NAME)}
          maxLength={100}
          validate={this.templateRenameValidator}
          dataTestName="rename-template-modal"
        />
      </DialogPanel>
    );
  }
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    catalogTemplates: getCustomCatalogTemplates(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      deleteCatalogTemplate,
      hideDialogPanel,
      renameCatalogTemplate,
      showDialogPanel,
      showModalPrompt,
    })
)(injectIntl(CatalogTemplatesDialog));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CatalogTemplatesDialog.js